import { Breadcrumb, Button, Divider, Input, Layout, Modal, Skeleton, Tag, notification, theme } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { apiCreateSplitPlan } from "../../../api/splitPlans";
import { apiDetailsZoopPlan } from "../../../api/zoopPlan";
import { isSuperUser } from "../../../helpers/user";
import BaseTaxTable from "./BaseTaxTable/BaseTaxTable";
import "./ZoopBasePlanDetails.scss";

const ZoopBasePlanDetails = () => {
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const allowed = isSuperUser();

  const pathname = window.location.pathname;
  const parts = pathname.split("/");
  const id = parts[parts.length - 1];

  const [plan, setPlan] = useState();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!allowed || !id) {
      setLoading(false);
      return;
    }
    apiDetailsZoopPlan(id)
      .then((json) => {
        setPlan(json);
      })
      .catch(() => {
        notification.error({
          message: "Erro",
          description: "Não foi possível carregar o plano base.",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  const yesNo = (value) => (value ? "Sim" : "Não");

  const yesNoText = (value) => {
    if (value === undefined || value === null) {
      return "";
    }
    return String(value).toLowerCase() === "true" ? "Sim" : "Não";
  };

  const openCreate = () => {
    setNewName(plan?.name ? `${plan.name} - Divisão` : "");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await apiCreateSplitPlan({
        name: newName.trim(),
        zoop_tax_plan: plan.id,
        split_details: "{}",
        split_details_online: "{}",
      });
      const json = await response.json();
      notification.success({
        message: "Sucesso!",
        description: "Plano de divisão criado com sucesso!",
      });
      setCreateOpen(false);
      navigate(`/planos-e-taxas/detalhe/${json.id}`);
    } catch (error) {
      notification.error({
        message: "Erro",
        description: "Não foi possível criar o plano de divisão.",
      });
    } finally {
      setCreating(false);
    }
  };

  const properties = [
    { label: "Nome", value: plan?.name },
    { label: "Descrição", value: plan?.description },
    { label: "ID Zoop", value: plan?.zoop_id },
    { label: "Ativo", value: yesNo(plan?.is_active) },
    { label: "Visível", value: yesNoText(plan?.visible) },
    { label: "Intervalo", value: plan?.interval },
    { label: "Contagem de intervalo", value: plan?.interval_count },
    { label: "Custo de setup", value: plan?.setup_cost },
    { label: "Carência (minutos)", value: plan?.grace_period_in_minutes },
    { label: "Pré-pago", value: yesNo(plan?.prepaid) },
    { label: "Valor máximo", value: plan?.maximum_amount },
    { label: "Duração (meses)", value: plan?.duration_in_months },
    { label: "Data de início", value: plan?.date_start },
    { label: "Data de fim", value: plan?.date_end },
    { label: "Nome na fatura", value: plan?.invoice_name },
    { label: "Padrão por transação", value: yesNo(plan?.is_default_per_transaction) },
  ];

  const propertyRows = [];
  for (let index = 0; index < properties.length; index += 2) {
    propertyRows.push(properties.slice(index, index + 2));
  }

  const sections = [
    { title: "2. Boleto & Pix", mode: "others" },
    { title: "3. Vendas Presenciais", mode: "physical" },
    { title: "4. Vendas Online", mode: "online" },
  ];

  return (
    <div className="ZoopBasePlanDetails">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "flex-end" }}>
            <h2 className="title-page">{plan?.name}</h2>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {plan && (
                <Tag color={plan.is_active ? "green" : "red"}>
                  {plan.is_active ? "Ativo" : "Inativo"}
                </Tag>
              )}
              {plan && (
                <Button type="primary" onClick={openCreate}>
                  Criar plano de divisão
                </Button>
              )}
            </div>
          </div>
          <Divider />
          <Breadcrumb.Item>Planos base Zoop</Breadcrumb.Item>
          <Breadcrumb.Item>{plan?.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Content
          style={{
            padding: 24,
            margin: "30px 0 0 0",
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <div className="container-subtitle">
            <h3 className="subtitle-page">1. Propriedades gerais</h3>
          </div>
          <Divider />
          {loading ? (
            <Skeleton active />
          ) : (
            <div className="container-inputs">
              {propertyRows.map((row, rowIndex) => (
                <div className="container-two" key={rowIndex}>
                  {row.map((property) => (
                    <div key={property.label}>
                      <p>{property.label}</p>
                      <Input disabled value={property.value ?? ""} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Content>

        {sections.map((section) => (
          <Content
            key={section.mode}
            style={{
              padding: 24,
              margin: "30px 0 0 0",
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <div className="container-subtitle">
              <h3 className="subtitle-page">{section.title}</h3>
            </div>
            <Divider />
            {loading ? <Skeleton active /> : plan && <BaseTaxTable zoopPlan={plan} mode={section.mode} />}
          </Content>
        ))}

        <Modal
          title="Criar plano de divisão"
          open={createOpen}
          onOk={handleCreate}
          onCancel={() => setCreateOpen(false)}
          okText="Criar"
          cancelText="Cancelar"
          confirmLoading={creating}
          okButtonProps={{ disabled: !newName.trim() }}
        >
          <p>O plano será criado sem divisões. Você poderá defini-las na próxima tela.</p>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome do plano de divisão"
          />
        </Modal>
      </Layout>
    </div>
  );
};

export default ZoopBasePlanDetails;
